import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BASE_URL } from '../../utils/BASE_URL';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const initialState = {
    categories: [],
    loading: false,
    error: null,
    message: null
};

// Handle Errors helper 
const handleErrors = (error, rejectWithValue) => {
    const errorMessage = error.response?.data?.message || 'An error occurred';
    return rejectWithValue(error.response?.data || { message: errorMessage });
};

// Create Category
export const createCategory = createAsyncThunk(
    'category/create',
    async (data, { rejectWithValue }) => {
        try {
            const response = await axios.post(`${BASE_URL}/categories`, data, { withCredentials: true });
            toast.success(response.data.message || 'Category created successfully');
            return response.data;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to create category';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

// Get All Categories
export const getAllCategories = createAsyncThunk(
    'category/getAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${BASE_URL}/categories`);
            return response.data.data;
        } catch (error) {
            return handleErrors(error, rejectWithValue);
        }
    },
    {
        condition: (_, { getState }) => {
            const { category } = getState();
            if (category.loading || category.categories.length > 0) {
                return false;
            }
        }
    }
);

// Update Category
export const updateCategory = createAsyncThunk(
    'category/update',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`${BASE_URL}/categories/${id}`, data, { withCredentials: true });
            toast.success(response.data.message || 'Category updated successfully');
            return response.data;
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to update category';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

// Delete Category
export const deleteCategory = createAsyncThunk(
    'category/delete',
    async (id, { rejectWithValue }) => {
        try {
            const response = await axios.delete(`${BASE_URL}/categories/${id}`, { withCredentials: true });
            toast.success(response.data.message || 'Category deleted successfully');
            return { id, message: response.data.message };
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Failed to delete category';
            toast.error(errorMsg);
            return handleErrors(error, rejectWithValue);
        }
    }
);

export const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {
        clearMessage: (state) => {
            state.message = null;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Create
            .addCase(createCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(createCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.categories.push(action.payload.category);
                state.message = action.payload.message;
            })
            .addCase(createCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Get All
            .addCase(getAllCategories.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload;
            })
            .addCase(getAllCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Update
            .addCase(updateCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateCategory.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.categories.findIndex(c => c._id === action.payload.category._id);
                if (index !== -1) {
                    state.categories[index] = action.payload.category;
                }
                state.message = action.payload.message;
            })
            .addCase(updateCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            })
            // Delete
            .addCase(deleteCategory.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = state.categories.filter(c => c._id !== action.payload.id);
                state.message = action.payload.message;
            })
            .addCase(deleteCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message;
            });
    }
});

export const { clearMessage, clearError } = categorySlice.actions;
export default categorySlice.reducer;
